import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, onSnapshot, Timestamp } from "firebase/firestore";
import { db, auth } from "../firebase/fire";
import moment from 'moment-timezone';
import { FaCheckCircle, FaUser, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';
import { arrayUnion } from "firebase/firestore";
import { FaBars, FaTimes } from 'react-icons/fa';
import { FiCalendar, FiUsers } from 'react-icons/fi';
import LogoComponent from "./LogoComponent";
import { useReminders } from "./ReminderContext";
import 'moment-duration-format';
import LoadingOverlay from "./LoadingOverlay";
import { LogoContext } from "./LogoContext";
import { useClassAccordion } from "./ClassAccordionContext";
import Modal from "./Modal";

const MemberLogin = () => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [gymAttendance, setGymAttendance] = useState([]);
  const [activeTab, setActiveTab] = useState('current');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [loginMode, setLoginMode] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [checkInSuccess, setCheckInSuccess] = useState(null);
  const { checkReminders } = useReminders();
  const [searchTerm, setSearchTerm] = useState('');

  const [logoUrl, setLogoUrl] = useContext(LogoContext);
  const [barcode, setBarcode] = useState("");

  // Get class data from context instead of managing it locally
  const { todayClasses, setTodayClasses, expandedClassId, setExpandedClassId, classAttendance, setClassAttendance } = useClassAccordion();

  const [reminders, setReminders] = useState(() => {
    const savedReminders = localStorage.getItem('classReminders');
    return savedReminders ? JSON.parse(savedReminders) : {};
  });

  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [newAgreement, setNewAgreement] = useState(null);
  const [oldAgreement, setOldAgreement] = useState(null);
  const [agreementDate, setAgreementDate] = useState(null);
  const [hasAcceptedNewAgreement, setHasAcceptedNewAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  // Trigger Cloud Function to refresh Stripe data
  const refreshStripeData = async () => {
    try {
      const STRIPE_EXPORT_FUNCTION_URL = 'https://us-central1-little-power.cloudfunctions.net/exportStripeDataHTTP';
      console.log('Refreshing Stripe payment data...');
      const response = await fetch(STRIPE_EXPORT_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        console.warn('Stripe refresh returned status:', response.status);
      }
      console.log('Stripe data refreshed');
    } catch (error) {
      console.warn('Could not refresh Stripe data, using cached:', error.message);
      // Continue anyway - don't block check-in if refresh fails
    }
  };

  const getStripePaymentStatusForMember = async (stripeCustomerId) => {
    if (!stripeCustomerId) return null;

    try {
      // Check the webhook-maintained subscriptions doc first
      let subsForCustomer = null;

      const subsDoc = await getDoc(doc(db, "stripe_exports", "subscriptions"));
      if (subsDoc.exists()) {
        const subscriptions = subsDoc.data().data || [];
        subsForCustomer = subscriptions.filter(sub => sub.customer === stripeCustomerId);
      }

      // Fall back to the bulk-export doc if the webhook-maintained doc has nothing for this customer
      if (!subsForCustomer || subsForCustomer.length === 0) {
        const latestDoc = await getDoc(doc(db, "stripe_exports", "latest"));
        if (latestDoc.exists()) {
          const subscriptions = latestDoc.data()?.subscriptions || [];
          subsForCustomer = subscriptions.filter(sub => sub.customer === stripeCustomerId);
        }
      }

      // No subscription data found for this customer in either export — data unavailable, don't block
      if (!subsForCustomer || subsForCustomer.length === 0) {
        return null;
      }

      // We have subscription data for this customer — true only if one is actually active
      return subsForCustomer.some(sub => sub.status === 'active');
    } catch (error) {
      console.error("Error loading Stripe subscription data:", error);
      return null;
    }
  };

  const openPdfInNewTab = (pdfBase64, title = "PDF Document") => {
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
        </head>
        <body style="margin:0">
          <embed src="${pdfBase64}" type="application/pdf" width="100%" height="100%" />
        </body>
      </html>
    `);
      newWindow.document.close();
    } else {
      alert("Popup blocked! Please allow popups for this website.");
    }
  };

  const handleSkipAgreement = () => {
    setShowAgreementModal(false);
    navigate("/member-dashboard", { state: { user: auth.currentUser.uid } });
  };

  const handleAcceptNewAgreement = async () => {
    try {
      setLoading(true);
      const userDocRef = doc(db, "members", auth.currentUser.uid);
      await updateDoc(userDocRef, {
        signedAgreement: newAgreement.fileData,
        agreementDate: Timestamp.now(),
      });
      setHasAcceptedNewAgreement(true);
      setShowAgreementModal(false);
      setLoading(false);
      navigate("/member-dashboard", { state: { user: auth.currentUser.uid } });
    } catch (error) {
      console.error("Error accepting new agreement:", error);
      setLoading(false);
    }
  };

  const fetchAllMembers = async () => {
    try {
      const ADMIN_ID = 'Shc0onQqfbejDHpZ99mvvtJthoz1';
      const membersRef = collection(db, "members");
      const querySnapshot = await getDocs(membersRef);

      const allMembers = querySnapshot.docs
        .filter(doc => doc.id !== ADMIN_ID)
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          lastLogin: doc.data().lastLogin?.toDate() || null,
          membershipStart: doc.data().membershipStart?.toDate() || null
        }));

      return allMembers;
    } catch (error) {
      console.error("Error fetching members:", error);
      return [];
    }
  };

  const classCardBackgrounds = [
    'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    'linear-gradient(135deg, #0b3a4c 0%, #0f766e 100%)',
    'linear-gradient(135deg, #1f2937 0%, #334155 100%)',
  ];

  const getClassBackgroundStyle = (classId) => {
    if (!classId) return classCardBackgrounds[0];

    let hash = 0;
    for (let i = 0; i < classId.length; i += 1) {
      hash = (hash * 31 + classId.charCodeAt(i)) | 0;
    }
    const index = Math.abs(hash) % classCardBackgrounds.length;
    return classCardBackgrounds[index];
  };

  // Helper to normalize Firestore Timestamp / seconds object / ISO string to JS Date
  const parseFirestoreDate = (val) => {
    if (!val) return null;
    if (typeof val.toDate === 'function') return val.toDate();
    if (val.seconds) return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    const handleWeeklyResets = async () => {
      try {
        const members = await fetchAllMembers();
        const now = moment().tz('Pacific/Auckland');
        const lastSunday = moment().tz('Pacific/Auckland').day(0).startOf('day');

        const membershipDetailsRef = collection(db, "membershipDetails");
        const membershipDetailsSnapshot = await getDocs(membershipDetailsRef);
        const membershipDetails = membershipDetailsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        await Promise.all(members.map(async (member) => {
          const membership = membershipDetails.find(md => md.name === member.status);

          if (membership && membership.classesPerWeek && !isNaN(parseInt(membership.classesPerWeek))) {
            const lastReset = member.lastWeekReset
              ? moment(member.lastWeekReset.toDate()).tz('Pacific/Auckland')
              : moment().tz('Pacific/Auckland').day(0).startOf('day');

            if (now.isAfter(lastSunday) && lastReset.isBefore(lastSunday)) {
              try {
                await updateDoc(doc(db, "members", member.id), {
                  weeklyClassesAttended: 0,
                  lastWeekReset: serverTimestamp()
                });
              } catch (updateError) {
                console.error("Error updating member:", updateError);
              }
            }
          }
        }));
      } catch (fetchError) {
        console.error("Error handling weekly resets:", fetchError);
      }
    };

    handleWeeklyResets();
    const interval = setInterval(handleWeeklyResets, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // REMOVED: fetchClassInformation and fetchAttendanceFromClassSchedule
  // These are now handled by ClassAccordionContext

  const navigate = useNavigate();

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const ClassAccordion = ({ cls, style }) => {
    const attendance = classAttendance[cls.id] || {};
    const isExpanded = expandedClassId === cls.id;
    const [attendeePage, setAttendeePage] = React.useState(1);
    const ITEMS_PER_PAGE = 10;

    const filteredAttendees = attendance?.attendees
      ?.filter(attendee =>
        attendee.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    React.useEffect(() => {
      setAttendeePage(1);
    }, [searchTerm]);

    const totalAttendees = filteredAttendees?.length || 0;
    const totalPages = Math.ceil(totalAttendees / ITEMS_PER_PAGE);
    const pagedAttendees = filteredAttendees?.slice(
      (attendeePage - 1) * ITEMS_PER_PAGE,
      attendeePage * ITEMS_PER_PAGE
    );

    const attendeeCount = attendance?.attendees?.length || 0;
    const fillPct = cls.availableSpots > 0
      ? Math.min((attendeeCount / cls.availableSpots) * 100, 100)
      : 0;

    return (
      <div className="mb-2 rounded-xl overflow-hidden border border-white/10 shadow-sm" style={style}>
        {/* Header row — always visible */}
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3.5 text-left focus:outline-none hover:bg-white/5 transition-colors duration-150"
          onClick={() => setExpandedClassId(isExpanded ? null : cls.id)}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="shrink-0 bg-white/10 p-1.5 rounded-lg">
              <FiCalendar className="w-3.5 h-3.5 text-white/80" />
            </span>
            <span className="font-semibold text-sm text-white leading-snug">{cls.className}</span>
          </div>
          <span className="ml-2 shrink-0 text-white/40 text-base">{isExpanded ? '▴' : '▾'}</span>
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 flex flex-col gap-3">
            {/* Time row */}
            <div className="flex justify-between bg-white/10 rounded-lg px-3 py-2">
              <div>
                <p className="text-xs text-white/50 mb-0.5">Start Time</p>
                <p className="text-sm font-semibold text-white">{cls.startTime}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-white/50 mb-0.5">End Time</p>
                <p className="text-sm font-semibold text-white">{cls.endTime}</p>
              </div>
            </div>

            {/* Instructors */}
            <div>
              <p className="text-xs font-semibold text-white/60 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <FiUsers className="w-3.5 h-3.5 text-yellow-400" />
                Instructors
              </p>
              <div className="flex flex-col gap-1.5">
                {cls.coaches.map((coach, index) => {
                  const loginTime = attendance?.coachLoginTimes?.[coach] || "Not logged in";
                  const loggedIn = loginTime !== "Not logged in";
                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg ${loggedIn ? "bg-green-500/20" : "bg-red-500/20"}`}
                    >
                      <span className="text-sm font-medium text-white">{coach}</span>
                      <span className={`text-xs font-medium ${loggedIn ? "text-green-300" : "text-red-300"}`}>
                        {loginTime}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Capacity bar */}
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs text-white/60">Attendees</span>
                <span className="text-xs font-bold text-white">{attendeeCount} / {cls.availableSpots}</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-1.5">
                <div
                  className="bg-green-400 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${fillPct}%` }}
                />
              </div>
              <p className="text-xs text-white/50 mt-1">
                {cls.availableSpots - attendeeCount} spots available
              </p>
            </div>

            {/* Attendee list */}
            <div>
              <p className="text-xs font-semibold text-white/60 uppercase tracking-wide mb-2">Attendees</p>
              <input
                type="text"
                placeholder="Search attendees..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="mb-2 w-full px-3 py-2 rounded-lg text-xs text-gray-800 bg-white/90 placeholder-gray-400 border-0 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
              <div className="flex flex-col gap-1">
                {pagedAttendees?.length > 0 ? (
                  pagedAttendees.map((attendee, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center px-3 py-1.5 bg-white/10 rounded-lg"
                    >
                      <span className="text-xs text-white font-medium">{attendee.name} {attendee.lastName}</span>
                      <span className="text-xs text-white/50">{attendee.time}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-white/40 text-center py-2">No attendees yet</p>
                )}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setAttendeePage(p => Math.max(1, p - 1))}
                    disabled={attendeePage === 1}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                      attendeePage === 1
                        ? 'bg-white/5 text-white/20 cursor-not-allowed'
                        : 'bg-white/10 text-white hover:bg-white/20 active:scale-95'
                    }`}
                  >
                    ← Prev
                  </button>
                  <span className="text-xs text-white/50">
                    {attendeePage} / {totalPages} · {totalAttendees} members
                  </span>
                  <button
                    type="button"
                    onClick={() => setAttendeePage(p => Math.min(totalPages, p + 1))}
                    disabled={attendeePage === totalPages}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                      attendeePage === totalPages
                        ? 'bg-white/5 text-white/20 cursor-not-allowed'
                        : 'bg-white/10 text-white hover:bg-white/20 active:scale-95'
                    }`}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    localStorage.setItem('classReminders', JSON.stringify(reminders));
  }, [reminders]);

  // FIXED: This effect now just sets initial loading to false
  // Class data comes from ClassAccordionContext
  useEffect(() => {
    setInitialLoading(false);
  }, [todayClasses]);

  const userPickedModeRef = useRef(false);

  useEffect(() => {
    const selectableClasses = todayClasses.filter(
      (cls) => cls.status === 'current' || cls.status === 'upcoming'
    );

    // If the member already made a choice, don't let background refreshes of
    // todayClasses (30s interval, snapshot updates) override it — only react
    // if the class they had selected is no longer available.
    if (userPickedModeRef.current) {
      if (loginMode === 'checkin' && selectedClassId && !selectableClasses.some(cls => cls.id === selectedClassId)) {
        setSelectedClassId('');
      }
      return;
    }

    if (selectableClasses.length === 0) {
      setLoginMode('dashboard');
      setSelectedClassId('');
      return;
    }

    if (selectableClasses.length === 1) {
      setLoginMode('checkin');
      setSelectedClassId(selectableClasses[0].id);
      return;
    }

    setLoginMode('');
    setSelectedClassId('');
  }, [todayClasses]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email && !barcode) {
      alert("Please enter either email or scan your barcode.");
      setLoading(false);
      return;
    }

    let memberEmail = email.trim();
    let memberData = null;
    let barcodeUsed = false;

    if (barcode) {
      try {
        const membersRef = collection(db, "members");
        const q = query(membersRef, where("barcode", "==", barcode));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          if (!email) {
            alert("No member found for this barcode.");
            setLoading(false);
            return;
          }
        } else {
          const memberDoc = snapshot.docs[0];
          memberData = memberDoc.data();
          memberEmail = memberData.email.trim();
          barcodeUsed = true;
        }
      } catch (err) {
        console.error("Barcode lookup error:", err);
        if (!email) {
          alert("Error looking up member by barcode.");
          setLoading(false);
          return;
        }
      }
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, memberEmail, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        // Resend verification email
        try {
          await sendEmailVerification(user);
          alert("Please verify your email address before logging in. We've sent you a new verification email. Check your inbox and spam folder.");
        } catch (verifyError) {
          console.error('Error sending verification email:', verifyError);
          alert("Please verify your email address before logging in. Check your inbox for the verification email.");
        }
        await auth.signOut();
        setLoading(false);
        return;
      }

      const docRef = doc(db, "members", user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const memberData = docSnap.data();
        const memberStatus = memberData.status;

        if (memberStatus === "Pending") {
          alert("Your membership is pending approval by the admin.");
          await auth.signOut();
          setLoading(false);
          return;
        }

        if (memberStatus === "Expired") {
          alert("Your membership is Expired. Please contact the admin.");
          await auth.signOut();
          setLoading(false);
          return;
        }

        await updateDoc(docRef, {
          lastGymAttendance: serverTimestamp()
        });

        const userData = docSnap.data();
        const newAgreementRef = doc(db, "agreements", "member");
        const newAgreementDoc = await getDoc(newAgreementRef);
        const latestAgreement = newAgreementDoc.data();

        const memberAgreementDate = userData.agreementDate?.toDate();
        const latestAgreementDate = latestAgreement.timestamp?.toDate();

        if (!memberAgreementDate || memberAgreementDate < latestAgreementDate) {
          setOldAgreement(userData.signedAgreement);
          setAgreementDate(memberAgreementDate);
          setNewAgreement(latestAgreement);
          setShowAgreementModal(true);
          setLoading(false);
          return;
        }

        setLoading(false);
        navigate("/member-dashboard", { state: { user: user.uid } });

      } else {
        alert("Member data not found.");
        await auth.signOut();
        setLoading(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.code === "auth/invalid-credential" ||
        (error.error && error.error.message === "INVALID_LOGIN_CREDENTIALS")) {
        alert(barcodeUsed
          ? "Barcode found but password mismatch. Please try again."
          : "The email or password is incorrect. Please enter correct details.");
      } else {
        alert("An error occurred during login. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCheckIn = async (e) => {
    e?.preventDefault();
    if (!selectedClassId) {
      setError("Please select a class to check in.");
      showModal(
        "Please select a class above to check in. If you only want your dashboard, use the View Dashboard button."
      );
      return;
    }

    setLoading(true);
    setError("");

    let memberEmail = email.trim();
    let barcodeUsed = false;
    
    try {
      if (barcode) {
        const membersRef = collection(db, "members");
        const q = query(membersRef, where("barcode", "==", barcode));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const memberDoc = snapshot.docs[0];
          memberEmail = memberDoc.data().email.trim();
          barcodeUsed = true;
        } else if (!email) {
          alert("No member found for this barcode.");
          setLoading(false);
          return;
        }
      }

      const userCredential = await signInWithEmailAndPassword(auth, memberEmail, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        // Resend verification email
        try {
          await sendEmailVerification(user);
          alert("Please verify your email address before logging in. We've sent you a new verification email. Check your inbox and spam folder.");
        } catch (verifyError) {
          console.error('Error sending verification email:', verifyError);
          alert("Please verify your email address before logging in. Check your inbox for the verification email.");
        }
        await auth.signOut();
        setLoading(false);
        return;
      }

      const memberRef = doc(db, "members", user.uid);
      const memberSnap = await getDoc(memberRef);

      if (!memberSnap.exists()) {
        alert("Member data not found.");
        await auth.signOut();
        setLoading(false);
        return;
      }

      const memberData = memberSnap.data();
      if (memberData.status === "Pending") {
        alert("Your membership is pending approval.");
        await auth.signOut();
        setLoading(false);
        return;
      }
      if (memberData.status === "Expired") {
        const msg = "Your membership is Expired. Please contact the admin.";
        const speech = new SpeechSynthesisUtterance(msg);
        window.speechSynthesis.speak(speech);
        await auth.signOut();
        setLoading(false);
        return;
      }

      // Payment validation: MUST have either Stripe OR confirmed manual payment
      let stripeCustomerId = memberData.stripeCustomerId || null;
      const hasManualPayment = memberData.manualPaymentStatus === 'Paid';

      // Fetch membership details and (if needed) the Stripe customers export in parallel,
      // since neither depends on the other's result.
      const membershipQuery = query(
        collection(db, "membershipDetails"),
        where("name", "==", memberData.status)
      );
      const stripeCustomerLookupPromise = (!stripeCustomerId && memberData.email)
        ? getDoc(doc(db, "stripe_exports", "customers")).catch((e) => {
            console.warn("Could not look up Stripe customer by email:", e.message);
            return null;
          })
        : Promise.resolve(null);

      const [membershipSnap, customersDoc] = await Promise.all([
        getDocs(membershipQuery),
        stripeCustomerLookupPromise
      ]);

      if (membershipSnap.empty) {
        const msg = "Invalid membership details. Please contact your Instructor.";
        const speech = new SpeechSynthesisUtterance(msg);
        window.speechSynthesis.speak(speech);
        await auth.signOut();
        setLoading(false);
        return;
      }

      const membershipDetails = membershipSnap.docs[0].data();
      const now = moment();

      // If no stripeCustomerId stored, try matching by email in stripe_exports
      if (!stripeCustomerId && memberData.email && customersDoc?.exists()) {
        const customers = customersDoc.data().data || [];
        const matched = customers.find(
          c => c.email && c.email.trim().toLowerCase() === memberData.email.trim().toLowerCase()
        );
        if (matched) {
          stripeCustomerId = matched.id;
          // Auto-link so webhook and future syncs work correctly
          await updateDoc(doc(db, "members", auth.currentUser.uid), {
            stripeCustomerId: matched.id,
            stripeLastSyncedAt: serverTimestamp()
          });
        }
      }

      const hasStripePayment = !!stripeCustomerId;

      if (!hasStripePayment && !hasManualPayment) {
        const msg = `Payment status is not confirmed. Please contact the admin to set up payment before checking in.`;
        const speech = new SpeechSynthesisUtterance(msg);
        window.speechSynthesis.speak(speech);
        alert(msg);
        await auth.signOut();
        setLoading(false);
        return;
      }

      // If Stripe is linked, validate the payment using synced status
      if (hasStripePayment) {
        const stripePaymentStatus = memberData.stripePaymentStatus || 'Unknown';
        const stripeSubscriptionStatus = memberData.stripeSubscriptionStatus || 'unknown';

        // Active subscription is the primary signal — trust it
        const syncedStatusOk = stripeSubscriptionStatus === 'active' || stripePaymentStatus === 'Paid';

        if (!syncedStatusOk) {
          // Synced data looks stale or wrong — refresh then check live export
          await refreshStripeData();
          const hasValidPayment = await getStripePaymentStatusForMember(stripeCustomerId);

          // Only block if we explicitly confirmed no active subscription (false).
          // null means Stripe export data is unavailable — do not block the member.
          if (hasValidPayment === false) {
            const msg = `Payment status is not confirmed. Please update your payment method with the admin before checking in.`;
            const speech = new SpeechSynthesisUtterance(msg);
            window.speechSynthesis.speak(speech);
            alert(msg);
            await auth.signOut();
            setLoading(false);
            return;
          }
        }
      }

      const membershipEnd = parseFirestoreDate(memberData.membershipEndDate);
      if (!membershipDetails.isUnlimitedDuration && membershipEnd && moment(membershipEnd).isBefore(now)) {
        const msg = "Your membership has expired. Please renew to attend classes.";
        const speech = new SpeechSynthesisUtterance(msg);
        window.speechSynthesis.speak(speech);
        await auth.signOut();
        setLoading(false);
        return;
      }

      if (membershipEnd && membershipEnd !== 'Unlimited' && moment(membershipEnd).isBefore(now)) {
        const msg = "Your membership has expired. Please renew to attend classes.";
        const speech = new SpeechSynthesisUtterance(msg);
        window.speechSynthesis.speak(speech);
        await auth.signOut();
        setLoading(false);
        return;
      }

      if (memberData.classesRemaining !== 'Unlimited' && Number(memberData.classesRemaining) === 0) {
        const msg = "You have no classes remaining. Please contact your instructor.";
        const speech = new SpeechSynthesisUtterance(msg);
        window.speechSynthesis.speak(speech);
        await auth.signOut();
        setLoading(false);
        return;
      }

      if (!membershipDetails.isUnlimitedClasses && memberData.weeklyClassesAttended >= parseInt(membershipDetails.classesPerWeek || 0)) {
        const msg = "You have reached your weekly class limit. Please try again next week.";
        const speech = new SpeechSynthesisUtterance(msg);
        window.speechSynthesis.speak(speech);
        await auth.signOut();
        setLoading(false);
        return;
      }

      const totalClasses = parseInt(membershipDetails.totalClasses);
      if (!isNaN(totalClasses) && totalClasses > 0) {
        if (membershipDetails.isUnlimitedClasses && memberData.totalClassesAttended >= totalClasses) {
          const msg = "You have reached your total allowed classes. Please upgrade your membership.";
          const speech = new SpeechSynthesisUtterance(msg);
          window.speechSynthesis.speak(speech);
          await auth.signOut();
          setLoading(false);
          return;
        }
      }

      const selectedClass = todayClasses.find(cls => cls.id === selectedClassId);
      if (!selectedClass) {
        const msg = "Selected class not found.";
        const speech = new SpeechSynthesisUtterance(msg);
        window.speechSynthesis.speak(speech);
        await auth.signOut();
        setLoading(false);
        return;
      }

      const currentDate = moment().tz('Pacific/Auckland');
      const todayDateString = currentDate.format('YYYY-MM-DD');

      const classRef = doc(db, "classSchedule", selectedClassId);
      const classSnap = await getDoc(classRef);
      const classData = classSnap.data();

      if (classData.attendance && classData.attendance[todayDateString]) {
        const alreadyCheckedIn = classData.attendance[todayDateString].some(a => a.userId === user.uid);
        if (alreadyCheckedIn) {
          const msg = "You have already checked in to this class today.";
          const speech = new SpeechSynthesisUtterance(msg);
          window.speechSynthesis.speak(speech);
          setLoading(false);
          if (window.confirm("Would you like to view your dashboard?")) {
            navigate("/member-dashboard", { state: { user: user.uid } });
          } else {
            await auth.signOut();
          }
          return;
        }
      }

      const updatedMemberData = {
        totalClassesAttended: (memberData.totalClassesAttended || 0) + 1,
        weeklyClassesAttended: (memberData.weeklyClassesAttended || 0) + 1,
        lastGymAttendance: serverTimestamp()
      };

      if (!isNaN(totalClasses) && totalClasses > 0) {
        if (typeof memberData.classesRemaining === "number") {
          updatedMemberData.classesRemaining = Math.max(0, memberData.classesRemaining - 1);
        }
      }

      // These two writes target different documents and don't depend on each other's result.
      await Promise.all([
        updateDoc(classRef, {
          [`attendance.${todayDateString}`]: arrayUnion({
            userId: user.uid,
            time: moment().format("YYYY-MM-DD HH:mm:ss"),
          }),
        }),
        updateDoc(memberRef, updatedMemberData)
      ]);

      const successMessage = `Welcome ${memberData.name}, Enjoy your ${selectedClass.className} session!`;
      const speech = new SpeechSynthesisUtterance(successMessage);
      window.speechSynthesis.speak(speech);

      setCheckInSuccess({ memberData, selectedClass });
      setShowSuccessModal(true);
      setLoading(false);

    } catch (error) {
      console.error("Check-in error:", error);
      setLoading(false);
      if (error.code === "auth/invalid-credential") {
        alert(barcodeUsed
          ? "Barcode found but password mismatch. Please try again."
          : "The email or password is incorrect. Please enter correct details.");
      } else {
        alert("An error occurred during check-in. Please try again.");
      }
    }
  };

  const handleDashboardLogin = async (e) => {
    e.preventDefault();
    await handleLogin(e);
  };

  useEffect(() => {
    const fetchGymAttendance = async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, "members"),
        where("lastGymAttendance", ">=", today)
      );

      const querySnapshot = await getDocs(q);
      const attendanceData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        attendanceData.push({
          name: data.name,
          time: data.lastGymAttendance.toDate().toLocaleTimeString()
        });
      });
      setGymAttendance(attendanceData);
    };

    fetchGymAttendance();
    const interval = setInterval(fetchGymAttendance, 60000);

    return () => clearInterval(interval);
  }, []);

  const selectableClasses = todayClasses.filter(
    (cls) => cls.status === 'current' || cls.status === 'upcoming'
  );
  const fieldsEnabled = loginMode === 'dashboard' || (loginMode === 'checkin' && !!selectedClassId);

  return (
    <LoadingOverlay isLoading={loading}>
      <div className="flex flex-col min-h-screen bg-slate-50">
        {/* Mobile hamburger */}
        <button
          className="md:hidden fixed top-4 left-4 z-50 text-white bg-slate-800 p-2 rounded-xl shadow-md"
          onClick={toggleNav}
        >
          {isNavOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>

        <div className="flex flex-grow">
          {/* Sidebar */}
          <nav className={`w-72 bg-slate-900 text-white p-4 fixed h-full overflow-y-auto transition-transform duration-300 ease-in-out ${isNavOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 z-40 border-r border-white/5`}>
            <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-white/10">
              <FiCalendar className="w-4 h-4 text-indigo-400 shrink-0" />
              <h2 className="text-sm font-bold tracking-wider text-white uppercase">Today's Classes</h2>
            </div>
            {(() => {
              const currentCount = todayClasses.filter(c => c.status === 'current').length;
              const upcomingCount = todayClasses.filter(c => c.status === 'upcoming').length;
              const filteredClasses = todayClasses.filter(c => c.status === activeTab);
              return (
                <>
                  <div className="flex bg-white/10 rounded-xl p-1 mb-4">
                    <button
                      onClick={() => setActiveTab('current')}
                      className={`flex-1 py-2 px-2 rounded-lg text-xs font-semibold transition-all duration-200 ${activeTab === 'current' ? 'bg-white text-slate-800 shadow-sm' : 'text-white/60 hover:text-white'}`}>
                      Current ({currentCount})
                    </button>
                    <button
                      onClick={() => setActiveTab('upcoming')}
                      className={`flex-1 py-2 px-2 rounded-lg text-xs font-semibold transition-all duration-200 ${activeTab === 'upcoming' ? 'bg-white text-slate-800 shadow-sm' : 'text-white/60 hover:text-white'}`}>
                      Upcoming ({upcomingCount})
                    </button>
                  </div>
                  {filteredClasses.length === 0 ? (
                    <div className="text-center text-white/40 py-10 text-sm">No classes at this time</div>
                  ) : (
                    filteredClasses.map(cls => (
                      <ClassAccordion key={cls.id} cls={cls} style={{ background: getClassBackgroundStyle(cls.id) }} className="p-4 rounded-lg mb-4 text-white shadow-lg" />
                    ))
                  )}
                </>
              );
            })()}
          </nav>

          {/* Main content */}
          <div className="flex-1 md:ml-64 flex flex-col">
            {/* Top nav */}
            <div className="pl-16 pr-4 py-2.5 border-b border-gray-100 bg-white shadow-sm">
              <div className="flex flex-wrap justify-center items-center gap-2">
                <Link to="/member-signup"  className="whitespace-nowrap text-xs font-semibold px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors tracking-wide">New Register</Link>
                <Link to="/class-booking"  className="whitespace-nowrap text-xs font-semibold px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-900 transition-colors tracking-wide">Class Booking</Link>
                <Link to="/coach-login"    className="whitespace-nowrap text-xs font-semibold px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors tracking-wide">Instructor</Link>
                <Link to="/admin-login"    className="whitespace-nowrap text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors tracking-wide">Admin</Link>
              </div>
            </div>

            <div className="flex-1 flex flex-col items-center px-4 py-8">
              {/* Logo */}
              <img
                src={logoUrl}
                alt="Gym logo"
                className="h-24 sm:h-32 object-contain mb-6"
              />

              {/* Class selection */}
              <div className="w-full max-w-2xl mx-auto mb-6 p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <div className="flex items-center justify-center gap-2 mb-5">
                  <h3 className="text-base font-bold text-gray-800 tracking-wide uppercase text-center">Select a Class to Check In</h3>
                  {selectableClasses.length > 0 && (
                    <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5">Required</span>
                  )}
                </div>

                <div className="mb-3">
                  <label className={`flex items-start gap-3 p-3.5 border-2 rounded-xl cursor-pointer transition-all ${loginMode === 'dashboard' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/50'}`}>
                    <input
                      type="radio"
                      name="class-selection"
                      value="dashboard"
                      checked={loginMode === 'dashboard'}
                      onChange={() => { userPickedModeRef.current = true; setLoginMode('dashboard'); setSelectedClassId(''); setError(''); }}
                      className="mt-0.5 w-5 h-5 text-indigo-600 cursor-pointer"
                    />
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">View Dashboard</h4>
                      <p className="text-sm text-gray-500">Login without checking in to a class</p>
                    </div>
                  </label>
                </div>

                {selectableClasses.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {selectableClasses.map(cls => (
                      <label
                        key={cls.id}
                        className="flex items-start gap-3 p-3.5 border-2 rounded-xl cursor-pointer transition-all"
                        style={{
                          borderColor: selectedClassId === cls.id ? '#4f46e5' : '#e5e7eb',
                          backgroundColor: selectedClassId === cls.id ? '#eef2ff' : '#ffffff'
                        }}
                      >
                        <input
                          type="radio"
                          name="class-selection"
                          value={cls.id}
                          checked={selectedClassId === cls.id}
                          onChange={(e) => { userPickedModeRef.current = true; setLoginMode('checkin'); setSelectedClassId(e.target.value); setError(''); }}
                          className="mt-0.5 w-5 h-5 text-indigo-600 cursor-pointer"
                        />
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-gray-900">{cls.className}</h4>
                          <p className="text-sm text-gray-500">{cls.startTime} - {cls.endTime}</p>
                          <p className="text-xs font-medium mt-0.5">
                            {cls.status === 'current' ? (
                              <span className="text-green-600">🟢 Current Class</span>
                            ) : (
                              <span className="text-blue-600">🔵 Upcoming Class</span>
                            )}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-800">
                    <p className="font-semibold text-sm">No classes available today</p>
                    <p className="text-xs mt-1">You can still log in to view your dashboard.</p>

                  </div>
                )}
              </div>

              {/* Login form card */}
              <div className="w-full max-w-sm sm:max-w-md bg-white rounded-2xl shadow-lg border border-gray-100/80 px-6 py-8 sm:px-10 sm:py-10">
                <h2 className="text-base font-bold text-gray-800 mb-6 text-center tracking-wide uppercase">Member Login</h2>

                <form onSubmit={handleLogin} className="flex flex-col gap-4" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false">
                  {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Barcode</label>
                    <input
                      type="password"
                      value={barcode}
                      onChange={e => setBarcode(e.target.value)}
                      placeholder="Scan barcode"
                      autoComplete="off"
                      autoFocus={true}
                      disabled={!fieldsEnabled}
                      className={`w-full px-4 py-3 border rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition ${!fieldsEnabled ? 'bg-gray-100 border-gray-200 cursor-not-allowed' : 'border-gray-200'}`}
                    />
                    {barcode && <p className="text-green-600 text-xs font-medium mt-0.5">Barcode scanned!</p>}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</label>
                    <input
                      type="text"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      disabled={!fieldsEnabled}
                      className={`w-full px-4 py-3 border rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition ${!fieldsEnabled ? 'bg-gray-100 border-gray-200 cursor-not-allowed' : 'border-gray-200'}`}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Password</label>
                    <div className="relative flex items-center">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        required
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="off"
                        data-lpignore="true"
                        data-form-type="other"
                        spellCheck="false"
                        disabled={!fieldsEnabled}
                        className={`w-full px-4 py-3 pr-11 border rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition ${!fieldsEnabled ? 'bg-gray-100 border-gray-200 cursor-not-allowed' : 'border-gray-200'}`}
                      />
                      {password && (
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                          tabIndex="-1"
                        >
                          {showPassword ? <FaEyeSlash size={17} /> : <FaEye size={17} />}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end -mt-1">
                    <Link to="/forgot-password" className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline font-medium">
                      Forgot Password?
                    </Link>
                  </div>

                  <div className="flex flex-col gap-3 pt-1">
                    <button
                      type="button"
                      onClick={handleQuickCheckIn}
                      disabled={loading || !selectedClassId || !password}
                      className={`w-full bg-emerald-600 text-white py-3 rounded-xl text-sm font-bold shadow-sm tracking-wide transition-all duration-200 ${loading || !selectedClassId || !password ? 'opacity-40 cursor-not-allowed' : 'hover:bg-emerald-700 active:scale-[0.99]'}`}
                    >
                      {loading ? 'Checking In...' : <><FaCheck className="inline mr-2" />CHECK IN TO CLASS</>}
                    </button>

                    <button
                      type="button"
                      onClick={handleDashboardLogin}
                      disabled={loading || loginMode !== 'dashboard' || !password}
                      className={`w-full border border-gray-200 bg-white text-gray-600 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${loading || loginMode !== 'dashboard' || !password ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50 hover:border-gray-300 active:scale-[0.99]'}`}
                    >
                      {loading ? 'Processing...' : 'View Dashboard'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Agreement modal */}
        {showAgreementModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-2xl shadow-xl max-w-2xl w-full overflow-y-auto max-h-[90vh]">
              <h2 className="text-lg font-bold mb-1 text-gray-800 text-center">Updated Terms and Conditions</h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                There have been some Terms and Conditions changes since you last logged in.
              </p>

              <div className="mb-5 p-4 border border-gray-100 rounded-xl bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">You have previously:</p>
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" checked={true} disabled className="accent-indigo-600" />
                  <label className="text-sm text-gray-700">Accepted our previous Terms and Conditions</label>
                </div>
                {oldAgreement ? (
                  <button
                    onClick={() => openPdfInNewTab(oldAgreement, "Previously Accepted Agreement")}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline font-medium"
                  >
                    View Previously Accepted Agreement (PDF)
                  </button>
                ) : (
                  <p className="text-xs text-red-500">Previous agreement not available.</p>
                )}
              </div>

              <div className="mb-6 p-4 border border-indigo-100 rounded-xl bg-indigo-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">However, before you can continue you can:</p>
                {newAgreement?.fileData ? (
                  <button
                    onClick={() => openPdfInNewTab(newAgreement.fileData, "Updated Terms and Conditions")}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline font-medium mb-3 block"
                  >
                    View Updated Terms and Conditions (PDF)
                  </button>
                ) : (
                  <p className="text-xs text-red-500 mb-3">Updated agreement not available.</p>
                )}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="agreementCheck"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    className="mt-0.5 accent-indigo-600"
                  />
                  <span className="text-sm text-gray-700">I have read and accept the updated Terms and Conditions</span>
                </label>
              </div>

              <div className="flex justify-between gap-3">
                <button
                  onClick={handleSkipAgreement}
                  className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  Skip and Read Later
                </button>
                <button
                  onClick={handleAcceptNewAgreement}
                  disabled={!agreementAccepted}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg text-white transition-colors ${agreementAccepted ? "bg-green-600 hover:bg-green-700" : "bg-green-300 cursor-not-allowed"}`}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        )}

        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="large" showBackButton={true} backAction={() => navigate("/")}>
          {modalMessage}
        </Modal>

        {/* Check-in success modal */}
        {showSuccessModal && checkInSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
              <div className="text-center">
                <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Check-In Successful!</h3>
                <p className="text-gray-500 mb-6">Welcome {checkInSuccess.memberData?.name}, enjoy your {checkInSuccess.selectedClass?.className} session!</p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => navigate("/member-dashboard", { state: { user: auth.currentUser.uid } })}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    View My Dashboard
                  </button>
                  <button
                    onClick={async () => {
                      setEmail("");
                      setPassword("");
                      setBarcode("");
                      setSelectedClassId("");
                      setShowSuccessModal(false);
                      await auth.signOut();
                    }}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LoadingOverlay>
  );
};

export default MemberLogin;
